import React from 'react'
import moment from 'moment'
import { useEffect, useRef, useState, FC, CSSProperties } from 'react'
import closeIcon from 'assets/images/toolbar-close.svg'
import * as calendarService from '../../modules/CalendarService'
import EventCell from '../EventCell'
import styles from './style.module.scss'
import settings from 'popups/Settings'
import { getTodoInstances } from '../../modules/TodoUtils'
import i18n from 'i18n'

const EVENT_CELL_HEIGHT = 20

interface Todo {
  status?: string
}

interface CalendarInstance {
  id: string
  subject: string | null
  startDate: Date
  endDate: Date
  allday: boolean
  isBusy: boolean
  isCanceled: boolean
  isDeclined: boolean
  isMeeting: boolean
  myMeetingResponse: string | null
  isMyEvent: boolean
  color: string
  folderColor: string
  todo?: Todo
}

interface EventCellData {
  style: CSSProperties
  title: string
  key: string
  allday: boolean
  isFree: boolean
  instanceId: string
  endDate: Date
  date: Date
  isNotResponse: boolean
  isCanceled: boolean
  isDeclined: boolean
  isMyEvent: boolean
  color: string
  folderColor: string
  todo?: Todo
  isTodo: boolean
}

interface DayEventListProps {
  date: Date
  instances?: CalendarInstance[] | null
  onClose: () => void
  onDidRender: (element: HTMLDivElement) => void
  onCellClick: (event: EventCellData, cellElement: HTMLDivElement | null) => void
  onCellDoubleClick: (event: EventCellData, cellElement: HTMLDivElement | null) => void
}

const DayEventList: FC<DayEventListProps> = ({
  date,
  instances = null,
  onClose,
  onDidRender,
  onCellClick,
  onCellDoubleClick,
}) => {
  const [events, setEvents] = useState<EventCellData[]>([])
  const me = useRef<HTMLDivElement>(null)
  const locale = settings.getValue('general').locale

  const calculateEvents = async () => {
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000)

    const dayEvents: CalendarInstance[] = []
    if (instances) {
      dayEvents.push(...instances)
    } else {
      const inInstances: CalendarInstance[] = []
      const anInstances = calendarService
        .getInstances()
        .filter(
          (instance: CalendarInstance) =>
            instance.startDate < dayEnd && instance.endDate > dayStart,
        )
      inInstances.push(...anInstances)

      const aTodoInstances = await getTodoInstances(dayStart, dayEnd)
      if (aTodoInstances) {
        inInstances.push(...aTodoInstances)
      }
      const sorted = inInstances.sort((a, b) => {
        if (a.startDate.getTime() !== b.startDate.getTime()) {
          return a.startDate.getTime() - b.startDate.getTime()
        }

        if (a.todo && !b.todo) {
          return 1
        }
        if (b.todo && !a.todo) {
          return 1
        }
        if (a.todo?.status !== b.todo?.status) {
          return a.todo?.status === 'COMPLETED' ? 1 : -1
        }
        const aSubject = a.subject || ''
        const bSubject = b.subject || ''
        return aSubject.localeCompare(bSubject, locale)
      })
      dayEvents.push(...sorted)
    }

    const eventCells: EventCellData[] = dayEvents.map((instance, index) => {
      let title = instance.subject
      if (title == null || title.length === 0) {
        title = i18n.t('editor.value.noSubject')
      }
      if (instance.todo == null) {
        title = (instance.isCanceled ? 'Canceled: ' : '') + title
      }
      const cellInfo: EventCellData = {
        style: {
          height: EVENT_CELL_HEIGHT,
          minHeight: EVENT_CELL_HEIGHT,
          margin: '1px 0',
          borderRadius: '4px',
          position: 'relative',
        },
        title: `${instance.allday ? '' : moment(instance.startDate).format('HH:mm')} ${title}`,
        key: `day-event-${index}`,
        allday: instance.allday,
        isFree: !instance.isBusy,
        instanceId: instance.id,
        endDate: instance.endDate,
        date: date,
        isNotResponse: instance.isMeeting && instance.myMeetingResponse === null,
        isCanceled: instance.isCanceled,
        isDeclined: instance.isDeclined,
        isMyEvent: instance.isMyEvent,
        color: instance.color,
        folderColor: instance.folderColor,
        todo: instance.todo,
        isTodo: instance.todo != null,
      }
      return cellInfo
    })

    setEvents(eventCells)
  }

  useEffect(() => {
    calculateEvents()
  }, [instances])

  useEffect(() => {
    if (instances == null) {
      calendarService.addInstanceListener(calculateEvents)
    }

    return () => {
      if (instances == null) {
        calendarService.removeInstanceListener(calculateEvents)
      }
    }
  }, [date])

  const listHeight = useRef(0)
  useEffect(() => {
    const _sendHeight = () => {
      if (me.current) {
        const aHeight = me.current.clientHeight
        if (Math.floor(listHeight.current) !== Math.floor(aHeight)) {
          if (listHeight.current === 0 || listHeight.current > aHeight) {
            listHeight.current = aHeight
            onDidRender(me.current)
          }
        }
      }
    }
    setTimeout(() => {
      _sendHeight()
    }, 10)
  }, [])

  return (
    <div className={styles.container} ref={me}>
      <div className={styles.header}>
        <div className={styles.dateContainer}>
          <div className={styles.day}>{moment(date).format('ddd').toUpperCase()}</div>
          <div className={styles.date}>{date.getDate()}</div>
        </div>
        <img src={closeIcon} className={styles.closeButton} onClick={onClose} alt="close" />
      </div>
      <div className={styles.eventList}>
        {events.map((event) => (
          <EventCell
            {...({
              key: event.key,
              event: event,
              onCellClick: onCellClick,
              onCellDoubleClick: onCellDoubleClick,
              draggable: false,
            } as any)}
          />
        ))}
      </div>
    </div>
  )
}

export default DayEventList
